import { useCallback, useState } from 'react';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import type { ProcessFolderConfig } from './processFolderConfig';

interface State {
  isLoading: boolean;
  error: Error | null;
}

interface Result extends State {
  /**
   * Persist a folder-level Process configuration. Pass `null` to clear
   * the stored config — readers then fall back to the empty default.
   */
  setProcessFolderConfig: (
    folderId: string,
    config: ProcessFolderConfig | null,
  ) => Promise<void>;
}

export function useSetProcessFolderConfig(): Result {
  const authedFetch = useAuthenticatedFetch();
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const setProcessFolderConfig = useCallback(
    async (folderId: string, config: ProcessFolderConfig | null) => {
      setLoading(true);
      setError(null);
      try {
        const res = await authedFetch(
          `/api/folders/${encodeURIComponent(folderId)}/process-folder-config`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ config }),
          },
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
          throw new Error(body.error || `HTTP ${res.status}`);
        }
      } catch (e) {
        setError(e as Error);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [authedFetch],
  );

  return { setProcessFolderConfig, isLoading, error };
}
